import React, {useMemo, useRef, useState} from 'react';
import {Button, Empty, Modal, Select as AntSelect, Space, Table, Tooltip, Typography} from 'antd';
import {DeleteOutlined, DownOutlined, EditOutlined, RightOutlined} from '@ant-design/icons';
import {useAuth} from "../../../providers/AuthProvider";
import {useProjectForm} from "../../../providers/ProjectFormProvider";
import ExpenseForm from "./ExpenseForm";

const { Title, Text } = Typography;
const { Option } = AntSelect;

const ProjectContractExpensesModal = ({ visible, onClose }) => {
    const {auth} = useAuth();
    const formRef = useRef(null);
    const [showForm, setShowForm] = useState(false);
    const [formModel, setFormModel] = useState({});
    const [editingItem, setEditingItem] = useState(null);
    const [expandedClientFees, setExpandedClientFees] = useState(false);
    const [expandedTalentCost, setExpandedTalentCost] = useState(false);
    const [expandedBillableExpenses, setExpandedBillableExpenses] = useState(false);
    const [expandedNonBillableExpenses, setExpandedNonBillableExpenses] = useState(false);
    const [expandedBillableAdminFees, setExpandedBillableAdminFees] = useState(false);
    const [expandedNonBillableAdminFees, setExpandedNonBillableAdminFees] = useState(false);
    const {project, isLoading, isProjectUpdating: isDisabled,updateProjectExpenses, error, refetch} = useProjectForm();

    // For testing: Uncomment one of the lines below to override auth.canManageClients()
    const canManageClients = auth.canManageClients();
    // const canManageClients = true;  // Test as admin
    // const canManageClients = false; // Test as non-admin



    // Get expense types from Laravel config
    const expenseTypes = laravel.contract_line_item_types || [
        'Travel',
        'Software',
        'Supplies',
        'Equipment',
        'Training',
        'Licensing',
        'Other'
    ];

    // Get contract line items from project
    const contractLineItems = project?.contract_line_items || [];

    // Filter line items for non-admin users (hide non-billable expenses)
    const filteredLineItems = useMemo(() => {
        return project?.filteredExpenseLineItemsForAuth() || []
    }, [project?.contract_line_items || [], canManageClients]);

    // Map position_id to position name
    const getPositionName = (positionId) => {
        if (!positionId) return '-';
        const position = project?.positions?.find(p => p.id === positionId);
        return position?.title || position?.name || '-';
    };

    // Format currency with commas
    const formatCurrency = (value) => {
        return value.toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });
    };

    // Calculate client billable and talent reimbursable for each expense
    const expensesWithCalculations = useMemo(() => {
        return filteredLineItems.map(item => ({
            ...item,
            clientBillable: item.billable_amount || 0,
            talentReimbursable: item.is_reimbursable
                ? item.amount
                : 0
        }));
    }, [filteredLineItems]);


    // Calculate totals
    const totals = useMemo(() => {

        if (!project) {
            return {}
        }
        return {
            totalPlannedAmount: project.totalExpenseBudget(),
            totalClientBillable: 0,//project.totalClientBillableExpenseBudget(),
            totalTalentReimbursable: 0,//project.totalTalentReimbursableExpenseBudget(),
            totalProjectNonBillableExpenses: 0,//project.totalNonBillableExpenses(),
            totalProjectFees: project.totalProjectFeesWithExpenses(),
            totalDirectTalentCost: project.totalDirectTalentCostWithExpenses(),
            totalTakeRate: project.totalTakeRateIncludingExpenses(),
        };
    }, [expensesWithCalculations, project]);

    // Table columns definition
    const columns = useMemo(() => {
        const baseColumns = [
            {
                title: 'Type',
                dataIndex: 'type',
                key: 'type',
                render: (text, item) => (
                    <div className="flex flex-col gap-1 items-start max-w-40">
                        <span className="text-xs font-semibold">
                            {project?.constructor?.isContractItemTypeFee?.(item) ? 'Fee' : 'Expense'}
                        </span>
                        <span className="text-xs line-clamp-2 overflow-ellipsis">{text}</span>
                    </div>
                ),
            },
            {
                title: 'Role',
                dataIndex: 'position_id',
                key: 'position_id',
                render: (positionId) => <div className="relative max-w-40">
                    <span className="text-xs line-clamp-2 overflow-ellipsis">
                        {positionId ? getPositionName(positionId) : <i className="text-placeholder-gray">N/A</i>}
                    </span>
                </div>,
            },
        ];

        // Add admin-only columns

        if (canManageClients) {
            // baseColumns.push(
            //     {
            //         title: 'Billable to Client?',
            //         dataIndex: 'is_billable',
            //         key: 'is_billable',
            //         align: 'center',
            //         render: (value) => (
            //             <Text className={`text-xs whitespace-nowrap ${value ? 'text-success-dark' : 'placeholder-gray-100'}`}>
            //                 {value ? 'Yes' : 'No'}
            //             </Text>
            //         ),
            //     },
            //     {
            //         title: 'Reimbursable to Talent?',
            //         dataIndex: 'is_reimbursable',
            //         key: 'is_reimbursable',
            //         align: 'center',
            //         render: (value) => (
            //             <Text className={`text-xs whitespace-nowrap ${value ? 'text-info-dark' : 'placeholder-gray-100'}`}>
            //                 {value ? 'Yes' : 'No'}
            //             </Text>
            //         ),
            //     }
            // );
        }


        baseColumns.push(

            {
                title: 'Notes',
                dataIndex: 'memo',
                key: 'memo',
                width: '300px',
                render: (text, record) => {
                    const memoText = text || '-';
                    if (canManageClients && record.admin_notes) {
                        return (
                            <div className="flex flex-row items-start gap-2">
                                <span className="flex-grow text-xs line-clamp-6 overflow-ellipsis max-w-52">{memoText}</span>
                                <Tooltip
                                    title={
                                        <div>
                                            <strong>Admin Notes:</strong>
                                            <br />
                                            {record.admin_notes || <i style={{ color: '#999' }}>No Notes Provided</i>}
                                        </div>
                                    }
                                >
                                    <i className="fa fa-info-circle ml-2 text-primary" style={{ cursor: 'pointer' }} />
                                </Tooltip>
                            </div>
                        );
                    }
                    return <span className="text-xs line-clamp-4 overflow-ellipsis max-w-52">{memoText}</span>;
                },
            }
        );

        if (canManageClients) {
            baseColumns.push(
                {
                    title: 'Internal Cost',
                    dataIndex: 'amount',
                    key: 'amount',
                    align: 'right',
                    render: (value, item) => (
                        <Text strong className={`text-xs whitespace-nowrap ${((item.type === 'Admin Fee' || item.type === 'Background Check') && value > 0) ? 'text-danger' : ''}`}>
                            {(item.type !== 'Admin Fee' && item.type !== 'Background Check') ?'-' : `$${formatCurrency(value)}`}
                        </Text>
                    ),
                },
            )
        }
        if(canManageClients) {
            baseColumns.push({
                title: 'Planned Amount',
                dataIndex: 'amount',
                key: 'amount',
                align: 'right',
                render: (value, item) => (
                    <Text strong className={`text-xs whitespace-nowrap ${value > 0 ? 'text-black' : ''}`}>
                        {(item.type !== 'Admin Fee' && item.type !== 'Background Check') ? `$${formatCurrency(value)}` : '-'}
                    </Text>
                ),
            });
        }
        baseColumns.push({
            title: canManageClients ? 'Client Billable' : 'Amount',
            dataIndex: 'clientBillable',
            key: 'clientBillable',
            align: 'right',
            render: (value) => (
                <Text strong className={`text-xs whitespace-nowrap ${value > 0 ? 'text-success-dark' : ''}`}>
                    ${formatCurrency(value)}
                </Text>
            ),
        });

        // Add admin-only columns
        if (canManageClients) {
            baseColumns.push({
                title: 'Talent Reimbursable',
                dataIndex: 'talentReimbursable',
                key: 'talentReimbursable',
                align: 'right',
                render: (value, item) => (
                    <Text strong className={`text-xs whitespace-nowrap ${(item.type !== 'Admin Fee' && item.type !== 'Background Check' && value > 0) ? 'text-success-dark' : ''}`}>
                        {(item.type !== 'Admin Fee' && item.type !== 'Background Check') ? `$${formatCurrency(value)}` : '-'}
                    </Text>
                ),
            });
        }

        // Add Actions column for admin users
        if (canManageClients) {
            baseColumns.push({
                title: 'Actions',
                key: 'actions',
                align: 'center',
                render: (_, record) => {
                    return (
                        <Space>
                            <Button
                                type="link"
                                icon={<EditOutlined />}
                                onClick={() => {
                                    handleEdit(record)
                                }}
                                className="text-xs"
                                disabled={isDisabled || showForm || editingItem !== null}
                            />
                            <Button
                                type="link"
                                danger
                                icon={<DeleteOutlined />}
                                onClick={() => handleDelete(record)}
                                className="text-xs"
                                disabled={isDisabled || showForm || editingItem !== null}
                            />
                        </Space>
                    )
                },
            });
        }

        return baseColumns;
    }, [canManageClients, isDisabled, showForm, editingItem]);

    const handleEdit = (expense) => {
        setEditingItem(expense);
        setShowForm(false); // Close the add form if open
    };

    const handleDelete = (record) => {
        if (!window.confirm('Are you sure you want to delete this expense?')) {
            return;
        }

        const updatedItems = contractLineItems.filter(item => item.id !== record.id);
        const payload = { contract_line_items: updatedItems };
        return updateProjectExpenses(payload);
    };

    const handleCancelEdit = () => {
        setEditingItem(null);
    };



    // Table summary (footer) for totals
    const tableSummary = (pageData) => {
        const totalPlannedAmount = pageData.reduce((sum, item) => sum + ((item.type !== 'Admin Fee' && item.type !== 'Background Check') ? (item.amount || 0) : 0), 0);
        const totalClientBillable = pageData.reduce((sum, item) => sum + item.clientBillable, 0);
        const totalTalentReimbursable = pageData.reduce((sum, item) => sum + item.talentReimbursable, 0);

        // Non-admin columns: Expense Type, Position, Planned Amount, Notes, Client Billable
        // Admin columns: Expense Type, Position, Billable?, Reimbursable?, Planned Amount, Notes, Markup%, Client Billable, Talent Reimbursable, Actions

        if (canManageClients) {
            return (
                <>
                    <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                        {/* ID */}
                        {/* Type */}
                        {/* Role */}
                        {/* Reimbursable to Talent? */}
                        {/* Notes */}
                        <Table.Summary.Cell align="right" colSpan={4}>
                            <strong className="whitespace-nowrap">Totals:</strong>
                        </Table.Summary.Cell>
                        {/* Internal Cost */}
                        <Table.Summary.Cell align="right">
                            <strong className="whitespace-nowrap">${formatCurrency(project.totalInternalCostProjectFees())}</strong>
                        </Table.Summary.Cell>

                        {/* Planned Amount */}
                        <Table.Summary.Cell align="right">
                            <strong className="whitespace-nowrap">${formatCurrency(totalPlannedAmount)}</strong>
                        </Table.Summary.Cell>
                        {/* Client Billable */}
                        <Table.Summary.Cell align="right">
                            <Text strong className="text-success-dark whitespace-nowrap">
                                ${formatCurrency(totalClientBillable)}
                            </Text>
                        </Table.Summary.Cell>
                        {/* Talent Reimbursable */}
                        <Table.Summary.Cell align="right">
                            <Text strong className="text-info-dark whitespace-nowrap">
                                ${formatCurrency(totalTalentReimbursable)}
                            </Text>
                        </Table.Summary.Cell>
                        {/* Actions */}
                        <Table.Summary.Cell />
                    </Table.Summary.Row>
                </>
            );
        } else {
            return (
                <>
                    <Table.Summary.Row style={{ backgroundColor: '#fafafa' }}>
                        {/* Expense Type */}
                        {/* Expense ID */}
                        {/* Position */}
                        <Table.Summary.Cell colSpan={4} align="right">
                            <strong className="whitespace-nowrap">Total:</strong>
                        </Table.Summary.Cell>
                        {/*/!* Planned Amount *!/*/}
                        {/*<Table.Summary.Cell align="right">*/}
                        {/*    <strong className="whitespace-nowrap">${formatCurrency(totalPlannedAmount)}</strong>*/}
                        {/*</Table.Summary.Cell>*/}
                        {/* Notes */}
                        {/*<Table.Summary.Cell colSpan={1}/>*/}
                        {/* Client Billable */}
                        <Table.Summary.Cell align="right">
                            <Text strong className="text-success-dark whitespace-nowrap">
                                ${formatCurrency(totalClientBillable)}
                            </Text>
                        </Table.Summary.Cell>
                    </Table.Summary.Row>
                </>
            );
        }
    };

    if (!project) {
        return null;
    }


    // Helper component for expandable row
    const ExpandableRow = ({
        label,
        value,
        isExpanded,
        onToggle,
        children,
        hasBorder = true,
        hasChildren = true,
        breakdown = null
    }) => {
        const isEmpty = value === 0 && !hasChildren;

        return (
            <div className={hasBorder ? 'border-b border-gray-200' : ''}>
                <div
                    onClick={isEmpty ? undefined : onToggle}
                    className={`flex justify-between items-center px-4 py-3 transition-colors ${
                        isEmpty
                            ? 'opacity-50 cursor-default'
                            : `cursor-pointer ${isExpanded ? 'bg-gray-50' : 'bg-white hover:bg-gray-50'}`
                    }`}
                >
                    <div className="flex items-center gap-2">
                        {!isEmpty && (isExpanded ?
                            <DownOutlined className="text-xs" /> :
                            <RightOutlined className="text-xs" />
                        )}
                        {isEmpty && <span className="w-3" />}
                        <span className="font-semibold">{label}</span>
                    </div>
                    <div className="flex items-center gap-3">
                        {breakdown && (
                            <span className="text-xs text-gray-500">
                                {breakdown}
                            </span>
                        )}
                        <span className="font-bold text-[15px]">
                            ${formatCurrency(value)}
                        </span>
                    </div>
                </div>
                {isExpanded && !isEmpty && children}
            </div>
        );
    };

    // Helper component for detail rows
    const DetailRow = ({ label, value, isFirst = false, markup = null }) => (
        <div className={`flex justify-between items-center py-2 px-4 pl-10 text-[13px] ${
            isFirst ? 'border-t border-gray-200' : ''
        } bg-gray-50`}>
            <span className="text-gray-600">{label}</span>
            <div className="flex items-center gap-3">
                {markup && (
                    <span className="text-[11px] text-gray-400">
                        {markup}
                    </span>
                )}
                <span className="text-gray-600">
                    ${formatCurrency(value)}
                </span>
            </div>
        </div>
    );

    // Render Contract Value Overview
    const renderContractOverview = () => {
        if (!auth.canManageClients()) {
            return null;
        }
        const totalContractValue = project.contract_value || 0;
        const totalTalentPayout = project.talent_cost || 0;
        const positions = project.sortedPositions() || [];

        // Out of pocket expense calculations
        const billableExpenseLineItems = TB.filter(
            project.contract_line_items || [],
            item => project.constructor.isContractItemTypeExpense(item) && item.billable_amount
        );
        const nonBillableExpenseLineItems = TB.filter(
            project.contract_line_items || [],
            item => project.constructor.isContractItemTypeExpense(item) && !item.billable_amount
        );

        const totalBillableExpenses = project.totalBillableProjectExpenses() || 0;
        const totalCostOfBillableExpenses =  project.totalNonBillableExpensesFees() || 0;
        const totalNonBillableExpenses = project.totalNonBillableExpensesFees() || 0;
        const totalOutOfPocketExpenses = 0;//project.totalOutOfPocketExpenses() || 0;

        return (
            <div className="bg-white border-2 border-gray-300 rounded-lg overflow-hidden mb-6">
                {/* Header */}
                <div className="bg-gray-100 px-6 py-4 border-b-2 border-gray-300">
                    <Title level={4} className="mb-0">Project Budget</Title>
                </div>

                <div className="p-6">
                    {/* LABOR SECTION */}
                    <div className="mb-6">
                        <div className="bg-gray-50 px-4 py-2 border-b border-gray-300 font-bold text-sm mb-2">
                            LABOR
                        </div>

                        {/* Client Fees Row */}
                        <ExpandableRow
                            label="Client Fees"
                            value={totalContractValue}
                            isExpanded={expandedClientFees}
                            onToggle={() => setExpandedClientFees(!expandedClientFees)}
                            hasChildren={positions.length > 0}
                        >
                            {positions.map((position, index) => (
                                <DetailRow
                                    key={position.id}
                                    label={position.title}
                                    value={position.contract_value || 0}
                                    isFirst={index === 0}
                                />
                            ))}
                        </ExpandableRow>

                        {/* Direct Talent Cost Row */}
                        <ExpandableRow
                            label="Direct Talent Cost"
                            value={totalTalentPayout}
                            isExpanded={expandedTalentCost}
                            onToggle={() => setExpandedTalentCost(!expandedTalentCost)}
                            hasBorder={false}
                            hasChildren={positions.length > 0}
                        >
                            {positions.map((position, index) => (
                                <DetailRow
                                    key={position.id}
                                    label={position.title}
                                    value={position.talent_cost || 0}
                                    isFirst={index === 0}
                                />
                            ))}
                        </ExpandableRow>
                    </div>

                    {/* PROJECT EXPENSES SECTION */}
                    <div className="mb-6 pt-4 border-t border-gray-300">
                        <div className="bg-gray-50 px-4 py-2 border-b border-gray-300 font-bold text-sm mb-2">
                            PROJECT EXPENSES
                        </div>

                        {/* Billable Expenses Row */}
                        <ExpandableRow
                            label="Billable Expenses"
                            value={project.totalBillableProjectExpenses() || 0}
                            isExpanded={expandedBillableExpenses}
                            onToggle={() => setExpandedBillableExpenses(!expandedBillableExpenses)}
                            hasChildren={billableExpenseLineItems.length > 0}
                            // breakdown={`Expense Cost: $${formatCurrency(totalBillableExpenses)} + Markup Cost: $${formatCurrency(totalCostOfBillableExpenses)}`}
                        >
                            {billableExpenseLineItems.map((lineItem, index) => {
                                const baseAmount = lineItem.amount || 0;
                                const markupAmount = lineItem.billable_amount - baseAmount;
                                const markupPercent = (markupAmount / baseAmount) * 100;

                                return (
                                    <DetailRow
                                        key={lineItem.id || index}
                                        label={lineItem.type}
                                        value={lineItem.billable_amount || 0}
                                        isFirst={index === 0}
                                        markup={markupPercent > 0 ? `Expense Cost: $${formatCurrency(baseAmount)} + Markup Cost: $${formatCurrency(markupAmount)} (${markupPercent.toFixed(2)}%)` : null}
                                    />
                                );
                            })}
                        </ExpandableRow>

                        {/* Non-Billable Expenses Row */}
                        <ExpandableRow
                            label="Non-Billable Expenses"
                            value={project.totalNonBillableProjectExpenses()}
                            isExpanded={expandedNonBillableExpenses}
                            onToggle={() => setExpandedNonBillableExpenses(!expandedNonBillableExpenses)}
                            hasBorder={false}
                            hasChildren={nonBillableExpenseLineItems.length > 0}
                        >
                            {nonBillableExpenseLineItems.map((lineItem, index) => (
                                <DetailRow
                                    key={lineItem.id || index}
                                    label={lineItem.type}
                                    value={lineItem.amount || 0}
                                    isFirst={index === 0}
                                />
                            ))}
                        </ExpandableRow>
                    </div>

                    {/* PROJECT FEES SECTION */}
                    <div className="mb-6 pt-4 border-t border-gray-300">
                        <div className="bg-gray-50 px-4 py-2 border-b border-gray-300 font-bold text-sm mb-2">
                            PROJECT FEES
                        </div>



                        {/* Internal Cost Row */}
                        <ExpandableRow
                            label="Internal Cost"
                            value={project.totalInternalCostProjectFees() || 0}
                            isExpanded={expandedNonBillableAdminFees}
                            onToggle={() => setExpandedNonBillableAdminFees(!expandedNonBillableAdminFees)}
                            hasBorder={false}
                            hasChildren={TB.filter(project.contract_line_items,
                                item => project.constructor.isContractItemTypeFee(item) && item.amount
                            ).length > 0}
                        >
                            {TB.filter(project.contract_line_items,
                                item => project.constructor.isContractItemTypeFee(item) && item.amount
                            ).map((lineItem, index) => (
                                <DetailRow
                                    key={lineItem.id || index}
                                    label={lineItem.type}
                                    value={lineItem.amount || 0}
                                    isFirst={index === 0}
                                />
                            ))}
                        </ExpandableRow>
                        {/* Billable Fees Row */}
                        <ExpandableRow
                            label="Billable Fees"
                            value={project.totalBillableProjectFees()}
                            isExpanded={expandedBillableAdminFees}
                            onToggle={() => setExpandedBillableAdminFees(!expandedBillableAdminFees)}
                            hasBorder={false}
                            hasChildren={TB.filter(project.contract_line_items,
                                item => project.constructor.isContractItemTypeFee(item) && item.billable_amount
                            ).length > 0}
                        >
                            {TB.filter(project.contract_line_items,
                                item => project.constructor.isContractItemTypeFee(item) && item.billable_amount
                            ).map((lineItem, index) => (
                                <DetailRow
                                    key={lineItem.id || index}
                                    label={lineItem.type}
                                    value={lineItem.billable_amount || 0}
                                    isFirst={index === 0}
                                />
                            ))}
                        </ExpandableRow>

                    </div>

                    {/* PROJECT TOTALS SECTION */}
                    <div className="pt-6 border-t-2 border-gray-400 mt-2">
                        <div className="space-y-2">
                            {/* Client Contract Value */}
                            <div className="flex justify-between items-center py-2 px-2 border-b border-gray-200">
                                <span className="font-semibold">Client Contract Value</span>
                                <span className="font-bold text-lg">
                                    ${formatCurrency(project.totalProjectFeesWithExpenses())}
                                </span>
                            </div>

                            {/* Total Project Related Costs */}
                            <div className="flex justify-between items-center py-2 px-2 border-b border-gray-200">
                                <span className="font-semibold">Total Project Related Costs</span>
                                <span className="font-bold text-lg">
                                    ${formatCurrency(
                                        totalTalentPayout +
                                        project.totalProjectExpenseBudget() +
                                        project.totalInternalCostProjectFees()
                                        // (project.totalCostOfBillableAdminFeeExpenses() || 0) +
                                        // (project.totalNonBillableAdminFeeExpenses() || 0)
                                    )}
                                </span>
                            </div>

                            {/* Total Take Rate */}
                            <div className="flex justify-between items-center py-2 px-2 border-b border-gray-200">
                                <span className="font-semibold">Total Take Rate</span>
                                <span className="font-bold text-lg">
                                    {(() => {
                                        const clientContractValue = project.totalProjectFeesWithExpenses()
                                            // +
                                            // (project.totalBillableOutOfPocketExpenses() || 0) +
                                            // (project.totalBillableAdminFeeExpenses() || 0);
                                        const totalProjectCosts = totalTalentPayout +
                                            project.totalProjectExpenseBudget() +
                                            project.totalInternalCostProjectFees()
                                            // +
                                            // (project.totalCostOfBillableAdminFeeExpenses() || 0) +
                                            // (project.totalNonBillableAdminFeeExpenses() || 0);
                                        const takeRate = clientContractValue > 0
                                            ? ((clientContractValue - totalProjectCosts) / clientContractValue) * 100
                                            : 0;
                                        return `${takeRate.toFixed(2)}%`;
                                    })()}
                                </span>
                            </div>

                            {/* Calculation Footer */}
                            <div className="bg-gray-50 px-2 py-3 text-xs text-gray-600 space-y-1 border-t border-gray-300 mt-2 -mx-6 -mb-6">
                                <div className="px-4">
                                    <strong className="text-gray-800">Client Contract Value:</strong> Client Contract Cost (${formatCurrency(totalContractValue)}) + Billable Expenses (${formatCurrency(project.totalBillableProjectExpenses())}) + Billable Fees (${formatCurrency( project.totalBillableProjectFees())})
                                </div>
                                <div className="px-4">
                                    <strong className="text-gray-800">Total Project Related Costs:</strong> Direct Talent Cost (${formatCurrency(totalTalentPayout)}) + Expense Cost (${formatCurrency(project.totalProjectExpenseBudget())}) + Internal Fee Cost (${formatCurrency(project.totalInternalCostProjectFees())})
                                </div>
                                <div className="px-4">
                                    <strong className="text-gray-800">Total Take Rate:</strong> ((Client Contract Value - Total Project Related Costs) / Client Contract Value) × 100
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <Modal
            title={(
                <div className="flex flex-col items-start gap-2">
                    <p className="text-lg font-semibold">
                        Project Expenses and Fees Budget
                    </p>
                    {canManageClients && (
                        <p className="text-sm">
                            Add relevant expense and fee line items for the project to align with the client SOW and plan project financials
                        </p>
                    )}
                </div>
            )}
            open={visible}
            onCancel={onClose}
            footer={null}
            width={1400}
        >
            <div className="flex flex-col items-stretch gap-4">
                <div style={{
                    backgroundColor: 'white',
                    padding: '20px 0',
                    borderRadius: '8px',
                }}>
                    {canManageClients && !showForm && (
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '16px'
                        }}>
                            <Button
                                type="primary"
                                onClick={() => setShowForm(true)}
                                disabled={isDisabled || editingItem !== null}
                            >
                                Add Expense or Fee
                            </Button>
                        </div>
                    )}

                    {visible && canManageClients && showForm && !editingItem && (
                        <ExpenseForm
                            model={null}
                            onCancel={() => setShowForm(false)}
                        />
                    )}

                    {visible && filteredLineItems.length > 0 ? (
                        <Table
                            columns={columns}
                            dataSource={expensesWithCalculations}
                            rowKey="id"
                            pagination={false}
                            scroll={{
                                x: 1300,
                            }}
                            tableLayout="auto"

                            summary={tableSummary}
                            expandable={{
                                expandedRowKeys: editingItem ? [editingItem.id] : [],
                                expandedRowRender: (record) => {
                                    if (editingItem && editingItem.id === record.id) {
                                        return (
                                            <div style={{
                                                backgroundColor: '#fafafa'
                                            }}>
                                                <ExpenseForm
                                                    model={editingItem}
                                                    onCancel={handleCancelEdit}
                                                />
                                            </div>
                                        );
                                    }
                                    return null;
                                },
                                expandIcon: () => null, // Hide the default expand icon
                                onExpand: (expanded, record) => {
                                    // Prevent manual expansion - only allow through edit button
                                    if (!expanded) {
                                        setEditingItem(null);
                                    }
                                }
                            }}
                        />
                    ) : (
                        !showForm && canManageClients && (
                            <Empty
                                description={
                                    <span>
                                        No expenses added yet.
                                        <br />
                                        Click "Add Expense or Fee" to create your first expense.
                                    </span>
                                }
                                style={{ padding: '40px 0' }}
                            />
                        )
                    )}
                </div>

                {/* Contract Value Overview */}
                {renderContractOverview()}
            </div>
        </Modal>
    );
};

export default ProjectContractExpensesModal;